import { useEffect, useState, useCallback } from 'react'
import {
  BarChart2, Search, TrendingUp, TrendingDown,
  CalendarDays, Zap, ArrowUpRight, ArrowDownRight,
} from 'lucide-react'
import { useEarnings } from '@/hooks/useEarnings'
import { clsx } from 'clsx'
import DataSection from '@/components/ui/DataSection'

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtNum(n: number | null | undefined, decimals = 2, prefix = ''): string {
  if (n == null) return 'N/A'
  const abs = Math.abs(n)
  if (abs >= 1e12) return `${prefix}${(n / 1e12).toFixed(decimals)}T`
  if (abs >= 1e9) return `${prefix}${(n / 1e9).toFixed(decimals)}B`
  if (abs >= 1e6) return `${prefix}${(n / 1e6).toFixed(decimals)}M`
  return `${prefix}${n.toFixed(decimals)}`
}

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

// ── Sub-components ───────────────────────────────────────────────────────────

function EarningsCalendarSection() {
  const { calendar, calendarLoading, fetchCalendar } = useEarnings()

  useEffect(() => {
    fetchCalendar(14)
  }, [fetchCalendar])

  return (
    <div className="card p-4">
      <h2 className="panel-title mb-3 flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-blue-light" />
        Upcoming Earnings (next 14 days)
      </h2>

      <DataSection
        loading={calendarLoading}
        empty={!calendarLoading && calendar.length === 0}
        emptyMessage="No upcoming earnings in the next 14 days."
        onRetry={() => fetchCalendar(14)}
        skeletonRows={5}
      >
        <div className="table-responsive overflow-x-auto">
          <table className="table-pro w-full text-sm">
            <thead>
              <tr>
                <th className="py-2 pr-4">Ticker</th>
                <th className="py-2 pr-4">Company</th>
                <th className="py-2 pr-4">Date</th>
                <th className="!text-right py-2 pr-4">Days</th>
                <th className="!text-right py-2 pr-4">Est. EPS</th>
                <th className="!text-center py-2">Time</th>
                <th className="!text-center py-2">Impact</th>
              </tr>
            </thead>
            <tbody>
              {calendar.map((e, i) => (
                <tr key={`${e.ticker}-${e.date}-${i}`}>
                  <td className="py-2 pr-4 font-mono font-semibold text-blue-light">{e.ticker}</td>
                  <td className="py-2 pr-4 text-text-primary">{e.name}</td>
                  <td className="py-2 pr-4 text-text-muted">{formatDate(e.date)}</td>
                  <td className="py-2 pr-4 text-right text-text-muted">{e.days_until}d</td>
                  <td className="py-2 pr-4 text-right text-text-primary font-mono">
                    {e.estimated_eps != null ? `$${e.estimated_eps.toFixed(2)}` : 'N/A'}
                  </td>
                  <td className="py-2 text-center">
                    <span className={e.time === 'BMO' ? 'badge-amber' : 'badge-blue'}>
                      {e.time}
                    </span>
                  </td>
                  <td className="py-2 text-center">
                    <span className={e.impact === 'High' ? 'badge-red' : 'badge-neutral'}>
                      {e.impact}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DataSection>
    </div>
  )
}

function EarningsHistorySection() {
  const { history, historyLoading, surprise, surpriseLoading, fetchHistory, fetchSurprise } = useEarnings()
  const [ticker, setTicker] = useState('')
  const [searched, setSearched] = useState('')

  const handleSearch = useCallback(() => {
    const t = ticker.trim().toUpperCase()
    if (!t) return
    setSearched(t)
    fetchHistory(t)
    fetchSurprise(t)
  }, [ticker, fetchHistory, fetchSurprise])

  return (
    <div className="card p-4">
      <h2 className="panel-title mb-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-blue-light" />
        Earnings History
      </h2>

      {/* Search bar */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Enter ticker (e.g. AAPL)"
          value={ticker}
          onChange={(e) => setTicker(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="input flex-1"
        />
        <button
          onClick={handleSearch}
          className="btn-primary text-sm"
        >
          Search
        </button>
      </div>

      <DataSection
        loading={historyLoading || surpriseLoading}
        empty={!historyLoading && !surpriseLoading && !searched}
        emptyMessage="Enter a ticker symbol above to view earnings history."
        skeletonRows={6}
      >
      {searched && history ? (
        <div className="space-y-4">
          {/* Summary stats */}
          {surprise?.summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 stagger-children">
              <div className="card p-3">
                <div className="panel-title mb-1">Beat Rate</div>
                <div className="text-lg font-bold text-green">{surprise.summary.beat_rate_pct}%</div>
              </div>
              <div className="card p-3">
                <div className="panel-title mb-1">Avg Surprise</div>
                <div className={clsx(
                  'text-lg font-bold',
                  surprise.summary.avg_surprise_pct >= 0 ? 'text-green' : 'text-rose-light',
                )}>
                  {surprise.summary.avg_surprise_pct >= 0 ? '+' : ''}{surprise.summary.avg_surprise_pct}%
                </div>
              </div>
              <div className="card p-3">
                <div className="panel-title mb-1">Avg Reaction</div>
                {/* The backend has no post-earnings price reaction data yet and
                    returns null — rendering "+%" green implied a real move. */}
                {surprise.summary.avg_stock_reaction_pct == null ? (
                  <div className="text-lg font-bold text-text-muted" title="Post-earnings price reaction isn't tracked yet">
                    N/A
                  </div>
                ) : (
                  <div className={clsx(
                    'text-lg font-bold',
                    surprise.summary.avg_stock_reaction_pct >= 0 ? 'text-green' : 'text-rose-light',
                  )}>
                    {surprise.summary.avg_stock_reaction_pct >= 0 ? '+' : ''}{surprise.summary.avg_stock_reaction_pct}%
                  </div>
                )}
              </div>
              <div className="card p-3">
                <div className="panel-title mb-1">Record</div>
                <div className="text-lg font-bold text-text-primary">
                  {surprise.summary.beats}W-{surprise.summary.misses}L
                </div>
              </div>
            </div>
          )}

          {/* History table */}
          <div className="table-responsive overflow-x-auto">
            <table className="table-pro w-full text-sm">
              <thead>
                <tr>
                  <th className="py-2 pr-3">Quarter</th>
                  <th className="!text-right py-2 pr-3">EPS Est</th>
                  <th className="!text-right py-2 pr-3">EPS Act</th>
                  <th className="!text-right py-2 pr-3">Surprise</th>
                  <th className="!text-right py-2 pr-3">Revenue</th>
                  <th className="!text-right py-2 pr-3">Reaction</th>
                  <th className="!text-center py-2">Result</th>
                </tr>
              </thead>
              <tbody>
                {history.quarters.map((q, i) => (
                  <tr key={i}>
                    <td className="py-2 pr-3 text-text-primary font-medium">{q.quarter}</td>
                    <td className="py-2 pr-3 text-right text-text-muted font-mono">${(q.eps_estimate ?? 0).toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right text-text-primary font-mono font-semibold">${(q.eps_actual ?? 0).toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right">
                      <span className={q.surprise_pct >= 0 ? 'delta-chip-pos' : 'delta-chip-neg'}>
                        {(q.surprise_pct ?? 0) >= 0 ? '+' : ''}{(q.surprise_pct ?? 0).toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right text-text-muted font-mono">{fmtNum(q.revenue_actual, 1, '$')}</td>
                    <td className="py-2 pr-3 text-right">
                      {q.stock_reaction_pct == null
                        ? <span className="font-mono text-text-muted">—</span>
                        : (
                          <span className={q.stock_reaction_pct >= 0 ? 'delta-chip-pos' : 'delta-chip-neg'}>
                            {q.stock_reaction_pct >= 0 ? '+' : ''}{q.stock_reaction_pct.toFixed(1)}%
                          </span>
                        )}
                    </td>
                    <td className="py-2 text-center">
                      {q.beat ? (
                        <span className="badge-green">
                          <ArrowUpRight className="w-3 h-3" /> Beat
                        </span>
                      ) : (
                        <span className="badge-red">
                          <ArrowDownRight className="w-3 h-3" /> Miss
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Surprise bar chart */}
          {surprise && (
            <div>
              <h3 className="panel-title mb-2">Beat/Miss History</h3>
              <div className="flex items-end gap-1 h-32">
                {surprise.surprises.slice().reverse().map((s, i) => {
                  const maxAbs = Math.max(...surprise.surprises.map(x => Math.abs(x.surprise_pct)), 1)
                  const height = Math.abs(s.surprise_pct) / maxAbs * 100
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end h-full relative group">
                      <div className="absolute -top-6 hidden group-hover:block card rounded px-2 py-1 text-xs text-text-primary shadow-lg z-10 whitespace-nowrap">
                        {s.period}: {(s.surprise_pct ?? 0) >= 0 ? '+' : ''}{(s.surprise_pct ?? 0).toFixed(1)}%
                      </div>
                      <div
                        className={clsx(
                          'w-full rounded-t transition-all',
                          s.beat ? 'bg-green' : 'bg-rose',
                        )}
                        style={{ height: `${Math.max(height, 4)}%` }}
                      />
                      <div className="text-[10px] text-text-muted mt-1 truncate w-full text-center">
                        {s.period.slice(-7)}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      ) : searched ? (
        <div className="text-text-muted text-sm">No earnings data found for {searched}.</div>
      ) : null}
      </DataSection>
    </div>
  )
}

function EarningsMoversSection() {
  const { movers, moversLoading, fetchMovers } = useEarnings()

  useEffect(() => {
    fetchMovers()
  }, [fetchMovers])

  return (
    <div className="card p-4">
      <h2 className="panel-title mb-3 flex items-center gap-2">
        <Zap className="w-4 h-4 text-amber-400" />
        Post-Earnings Movers Today
      </h2>

      {moversLoading ? (
        <div className="text-text-muted text-sm animate-pulse">Loading movers...</div>
      ) : !movers ? (
        <div className="text-text-muted text-sm">No data available.</div>
      ) : (
        <>
          {/* Summary row */}
          <div className="flex gap-4 mb-3 text-sm">
            <span className="text-text-muted">
              Reported: <span className="text-text-primary font-medium">{movers.summary.total_reported}</span>
            </span>
            <span className="text-green">
              Beats: {movers.summary.beats}
            </span>
            <span className="text-rose-light">
              Misses: {movers.summary.misses}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 stagger-children">
            {movers.movers.map((m, i) => (
              <div
                key={`${m.ticker}-${i}`}
                className="card hover-lift flex items-center justify-between p-3"
              >
                <div className="flex items-center gap-3">
                  <div className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
                    m.beat ? 'bg-green/15 text-green' : 'bg-rose/15 text-rose-light',
                  )}>
                    {m.beat ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  </div>
                  <div>
                    <div className="text-text-primary font-semibold text-sm">{m.ticker}</div>
                    <div className="text-text-muted text-xs">
                      EPS: ${(m.eps_actual ?? 0).toFixed(2)} vs ${(m.eps_estimate ?? 0).toFixed(2)}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div>
                    <span className={m.stock_move_pct >= 0 ? 'delta-chip-pos' : 'delta-chip-neg'}>
                      {(m.stock_move_pct ?? 0) >= 0 ? '+' : ''}{(m.stock_move_pct ?? 0).toFixed(1)}%
                    </span>
                  </div>
                  <div className={clsx(
                    'text-xs',
                    m.surprise_pct >= 0 ? 'text-green/70' : 'text-rose-light/70',
                  )}>
                    Surprise: {(m.surprise_pct ?? 0) >= 0 ? '+' : ''}{(m.surprise_pct ?? 0).toFixed(1)}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function EarningsPage() {
  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BarChart2 className="w-7 h-7 text-blue-light" />
        <div>
          <h1 className="page-title">Earnings Analysis</h1>
          <p className="text-text-muted text-sm">
            Track upcoming reports, historical performance, and post-earnings moves
          </p>
        </div>
      </div>

      {/* Calendar */}
      <EarningsCalendarSection />

      {/* Two column layout: History + Movers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <EarningsHistorySection />
        </div>
        <div>
          <EarningsMoversSection />
        </div>
      </div>
    </div>
  )
}
